import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import i18n from 'simple-react-i18n'
import echarts from 'echarts/lib/echarts'
import ReactECharts from 'echarts-for-react'
import { groupBy, isNil, keys, max, round, uniqBy, omit, template, range, orderBy, min, compact, sumBy, uniq } from 'lodash'
import { colorGradients, getUsedValues } from 'catchment/utils/CatchmentUtil'
import { CATCHMENT_IRRELEVANT_PESTICIDES, CATCHMENT_LIST_SUM_PESTICIDES, CATCHMENT_PESTICIDES, LAST_VALUE } from 'catchment/constants/CatchmentConstant'
import { shallowEqual, useSelector } from 'react-redux'
import useListIndexed from 'utils/customHook/useListIndexed'
import { getI18nOrLabel } from 'utils/StringUtil'
import DtoAnalysisLight from 'quality/dto/analyse/DtoAnalysisLight'
import PropTypes from 'prop-types'
import { exportExcelIcon, exportPictureIcon, fullScreenExitIcon, fullScreenIcon } from 'components/echart/EChartUtils'
import { getDate } from 'utils/DateUtil'
import { exportFile } from 'utils/ExportDataUtil'
import ExportFileModal from 'components/modal/ExportFileModal'
import useBoolean from 'utils/customHook/useBoolean'
import useApplicationSetting, { intParser, listStringParser } from 'utils/customHook/useApplicationSetting'
import useFetchAnalysis from 'utils/customHook/useFetchAnalysis'
import useFetchSumAnalysis from 'catchment/utils/useFetchSumAnalysis'
import CircularProgressWithLabel from 'components/progress/CircularProgressWithLabel'
import CatchmentAction from 'catchment/actions/CatchmentAction'
import useAbortController from 'utils/customHook/useAbortController'
import { blackColor, lightGrey } from 'utils/constants/ColorTheme'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import { LargerPropertiesParameterGraphModal } from 'quality/components/qualityComponents/ParameterGraph'
import { Button, Dialog, DialogActions, DialogContent } from '@mui/material'

const ExportModal = ({
    isOpen = false,
    close = () => { },

    analysis = [],
    exportName = '',
}) => {
    const {
        parameters,
        units,
    } = useSelector(store => ({
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
    }), shallowEqual)

    const parametersIndexed = useListIndexed(parameters, 'code')
    const unitsIndexed = useListIndexed(units)

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const onExport = ext => {
        const exportData = analysis.map(({ parameter, sampleDate, result, unit }) => {
            const isSumAnalysis = !parameter
            const data = isSumAnalysis ?
                {
                    date: { value: getDate(sampleDate), format: 'dd/MM/yyyy', cellType: 'date' },
                    value: { value: result, format: '0.00000', cellType: 'number' },
                } :
                {
                    date: { value: getDate(sampleDate), format: 'dd/MM/yyyy', cellType: 'date' },
                    parameterCode: parameter,
                    parameter: parametersIndexed[parameter]?.name ?? '',
                    value: { value: result, format: '0.00000', cellType: 'number' },
                    codeUnit: unit,
                    unit: unitsIndexed[unit]?.symbol ?? '',
                }
            return showSandreCode ? data : omit(data, ['parameterCode', 'codeUnit'])
        })
        exportFile({
            data: exportData.length ? [
                {
                    ...exportData[0],
                    headers: keys(exportData[0]),
                },
                ...exportData.slice(1),
            ] : [],
            exportType: ext,
            titleFile: exportName,
        }, true)
    }

    return (
        <ExportFileModal
            open={isOpen}
            onClose={close}
            maxWidth='md'
            data={[
                {
                    name: i18n.resultsTable,
                    formats: [{
                        type: i18n.excelFile,
                        callback: () => onExport('xlsx'),
                    }, {
                        type: i18n.csvFile,
                        callback: () => onExport('csv'),
                    }],
                },
            ]}
        />
    )
}

ExportModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    close: PropTypes.func.isRequired,
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)).isRequired,
    exportName: PropTypes.string.isRequired,
}

const GraphicPercent = ({
    analysis = [],
    thresholds = [],
    title = '',
    criteria = LAST_VALUE,
    colors = colorGradients[0],
    unit = '',
    parameter = '',
}) => {
    let echartRef = useRef(undefined)
    let displayToolbox = useRef(false)

    const {
        value: isExportOpen,
        setTrue: openExport,
        setFalse: closeExport,
    } = useBoolean(false)

    const {
        settings,
    } = useSelector(store => ({
        settings: store.AdministrationReducer.settings,
    }), shallowEqual)

    const formattedThresholds = [Number.NEGATIVE_INFINITY, ...thresholds]
    const seriesIndex = range(formattedThresholds.length)
    const usedValues = getUsedValues(analysis, criteria, settings).map(t => t.value)
    const groupAnalysis = groupBy(usedValues, v => max(seriesIndex.filter(nb => v >= formattedThresholds[nb])))

    const getBar = (key, group = [], color) => {
        const percentage = group.length && usedValues.length ? group.length / usedValues.length * 100 : 0
        return {
            value: [percentage, key],
            itemStyle: {
                color,
            },
            label: {
                show: true,
                position: 'right',
                color: blackColor,
                formatter: () => `${round(percentage, 1)} %`,
            },
        }
    }

    const getToolbox = () => ({
        top: '4%',
        right: '35px',
        showTitle: false,
        itemSize: 18,
        tooltip: {
            show: true,
            position: 'bottom',
        },
        feature: {
            saveAsImage: {
                show: displayToolbox.current,
                title: i18n.pictureExport,
                icon: exportPictureIcon,
                name: title,
            },
            myToolExport: {
                show: displayToolbox.current,
                title: i18n.excelExport,
                icon: exportExcelIcon,
                onclick: openExport,
            },
        },
    })

    const options = {
        title: {
            text: title,
            left: 'center',
            top: '10',
        },
        series: [{
            type: 'bar',
            data: [...seriesIndex.map(i => getBar(i, groupAnalysis[i], colors[i]))],
        }],
        xAxis: [{
            type: 'value',
            name: `${i18n.catchmentPercentage} (%)`,
            nameLocation: 'middle',
            nameGap: 25,
        }],
        yAxis: [{
            type: 'category',
            name: `${parameter} ${unit ? `(${unit})` : ''}`,
            data: seriesIndex,
            axisLabel: {
                formatter: v => {
                    const index = intParser(v)
                    if (index === 0) return `< ${thresholds[v]}`
                    if (index === seriesIndex.length - 1) return `> ${thresholds[v - 1]}`
                    if (index < seriesIndex.length) return `>= ${thresholds[v - 1]} ${i18n.and} < ${thresholds[v]}`
                    return i18n.noData
                },
            },
        }],
        grid: {
            top: '50',
            left: '5',
            right: '50',
            bottom: '80',
            containLabel: true,
            height: 200,
        },
        toolbox: getToolbox(),
    }
    return (
        <div
            onMouseOver={() => {
                displayToolbox.current = true
                echartRef.current?.getEchartsInstance().setOption({
                    toolbox: getToolbox(),
                })
            }}
            onMouseOut={() => {
                displayToolbox.current = false
                echartRef.current?.getEchartsInstance().setOption({
                    toolbox: getToolbox(),
                })
            }}
        >
            <ReactECharts
                echarts={echarts}
                option={options}
                notMerge={true}
                lazyUpdate={true}
                ref={e => {
                    echartRef.current = e
                }}
            />
            <ExportModal
                isOpen={isExportOpen}
                close={closeExport}
                analysis={analysis}
                exportName={title}
            />
        </div>
    )
}

GraphicPercent.propTypes = {
    analysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)),
    thresholds: PropTypes.arrayOf(PropTypes.number),
    title: PropTypes.string,
    criteria: PropTypes.string,
    colors: PropTypes.arrayOf(PropTypes.string),
    unit: PropTypes.string,
    parameter: PropTypes.string,
}

const ParameterGraphicPercent = ({
    parameter,
    ids = [],
    filters = {},
    colors = colorGradients[0],
    thresholds = [0.01, 0.1, 0.5, 2],
    unit = 'mg/L',
    title = '',
}) => {
    const {
        parametersProps,
    } = useSelector(store => ({
        parametersProps: store.ParameterReducer.parameters,
    }), shallowEqual)

    const parametersIndexed = useListIndexed(parametersProps, 'code')
    const parameterName = parametersIndexed[parameter]?.name

    const excludeProducers = useApplicationSetting('CATCHMENT_PRODUCERS_EXCLUDED', setting => setting?.split(',').filter(c => !!c).map(id => parseInt(id)) || [])

    const analysisFilters = useMemo(() => {
        return {
            lightMode: true,
            startDate: filters.firstStart,
            endDate: filters.firstEnd,
            stations: ids,
            parameters: [parameter],
            excludedProducers: excludeProducers,
            support: '3', // support 'eau'
        }
    }, [excludeProducers, filters.firstEnd, filters.firstStart, ids, parameter])

    const {
        analysis = [],
        isLoaded = false,
        progress = 0,
    } = useFetchAnalysis(analysisFilters)

    return (
        <div style={{ position: 'relative' }}>
            {!isLoaded && (<CircularProgressWithLabel value={progress} />) }
            <GraphicPercent
                analysis={!ids?.length ? [] : analysis}
                thresholds={thresholds}
                title={title || parameterName}
                unit={unit}
                criteria={filters.criteria}
                colors={colors}
                parameter={template(i18n.moleculeContent)({ molecule: parameterName })}
            />
        </div>
    )
}

ParameterGraphicPercent.propTypes = {
    parameter: PropTypes.string,
    ids: PropTypes.arrayOf(PropTypes.number),
    filters: PropTypes.shape({}), //
    colors: PropTypes.arrayOf(PropTypes.string),
    thresholds: PropTypes.arrayOf(PropTypes.number),
    unit: PropTypes.string,
    title: PropTypes.string,
}

const SumParameterGraphicPercent = ({
    filters = {},
    colors = colorGradients[0],
    thresholds = [0.01, 0.1, 0.5, 2],
    ids = [],
    title = '',
}) => {
    const excludeProducers = useApplicationSetting('CATCHMENT_PRODUCERS_EXCLUDED', setting => setting?.split(',').filter(c => !!c).map(id => parseInt(id)) || [])
    const relevantPesticides = useApplicationSetting(CATCHMENT_PESTICIDES, listStringParser)
    const irrelevantPesticides = useApplicationSetting(CATCHMENT_IRRELEVANT_PESTICIDES, listStringParser)
    const pesticides = useMemo(() => uniq([...relevantPesticides, ...irrelevantPesticides]), [relevantPesticides, irrelevantPesticides])
    const listSumPesticides = useApplicationSetting(CATCHMENT_LIST_SUM_PESTICIDES, listStringParser)

    const analysisFilters = useMemo(() => {
        return {
            lightMode: true,
            startDate: filters.firstStart,
            endDate: filters.firstEnd,
            stations: ids,
            parameters: listSumPesticides.length > 0 ? listSumPesticides : pesticides,
            quantificationControl: true,
            excludedProducers: excludeProducers,
            support: '3', // support 'eau'
        }
    }, [excludeProducers, filters.firstEnd, filters.firstStart, ids, listSumPesticides, pesticides])

    const {
        sumAnalysis = [],
        isLoaded = false,
        progress = 0,
    } = useFetchSumAnalysis(analysisFilters)

    return (
        <div style={{ position: 'relative' }}>
            {!isLoaded && (<CircularProgressWithLabel value={progress} />)}
            <GraphicPercent
                analysis={!ids.length ? [] : sumAnalysis}
                thresholds={thresholds}
                title={title || i18n.sumPesticides}
                unit='µg/L'
                criteria={filters.criteria}
                colors={colors}
                parameter={i18n.sumPesticides}
            />
        </div>
    )
}

SumParameterGraphicPercent.propTypes = {
    ids: PropTypes.arrayOf(PropTypes.number),
    filters: PropTypes.shape({}), //
    colors: PropTypes.arrayOf(PropTypes.string),
    thresholds: PropTypes.arrayOf(PropTypes.number),
    title: PropTypes.string,
}

const breakLine = (name = '', nbChar = 25) => {
    const splitName = name.replaceAll('_', ' ').split(' ')
    let temp = 0
    return splitName.reduce((acc, cur) => {
        if (!acc) {
            temp = cur.length
            return cur
        }
        if (temp + cur.length > nbChar) {
            temp = cur.length
            return `${acc}\n${cur}`
        }
        temp = temp + cur.length
        return `${acc} ${cur}`
    }, '')
}

const Pie = ({
    listCount = [],
    title = i18n.distributionQuantificationsByFamilyFirstPeriod,
    qualitoIds = [],
    startPeriod,
    endPeriod,
}) => {
    const {
        parameterGroups,
        parameterGroupLinks,
    } = useSelector(store => ({
        parameterGroups: store.ParameterReducer.parameterGroups,
        parameterGroupLinks: store.ParameterReducer.parameterGroupLinks,
    }), shallowEqual)

    const {
        value: isGraphOpen,
        setTrue: openGraph,
        setFalse: closeGraph,
    } = useBoolean(false)
    const {
        value: isFullScreen,
        setFalse: closeFullScreen,
        toggle: toggleFullSCreen,
    } = useBoolean(false)

    const echartRef = useRef(undefined)
    const echartDialogRef = useRef(undefined)
    const displayToolbox = useRef(false)

    const [popinData, setPopinData] = useState({ name: '', group: -1, value: 0 })

    const relevantPesticides = useApplicationSetting(CATCHMENT_PESTICIDES, listStringParser)
    const irrelevantPesticides = useApplicationSetting(CATCHMENT_IRRELEVANT_PESTICIDES, listStringParser)
    const pesticides = useMemo(() => uniq([...relevantPesticides, ...irrelevantPesticides]), [relevantPesticides, irrelevantPesticides])
    const excludeProducers = useApplicationSetting('CATCHMENT_PRODUCERS_EXCLUDED', setting => setting?.split(',').filter(c => !!c).map(id => parseInt(id)) || [])
    const parameterGroupsIndexed = useListIndexed(parameterGroups, 'code')
    const reduceParameterGroupLinks = useMemo(() => parameterGroupLinks.map(l => ({ parameter: l.parameterCode, groups: compact([l.usageGroupCode, l.usageGroupCode2, l.usageGroupCode3, l.usageGroupCode4, l.usageGroupCode5]) })), [parameterGroupLinks])
    const pesticidesParameterGroupLinks = useMemo(() => reduceParameterGroupLinks.filter(g => pesticides.includes(g.parameter)), [reduceParameterGroupLinks, pesticides])
    const popinParameters = useMemo(() => pesticidesParameterGroupLinks.filter(group => group.groups?.includes(popinData.group))?.map(link => link.parameter), [pesticidesParameterGroupLinks, popinData])

    const series = useMemo(() => ([
        {
            type: 'pie',
            radius: '100',
            center: ['30%', '50%'],
            data: listCount.map(({ groupCode, nb }) => ({
                name: getI18nOrLabel(parameterGroupsIndexed[groupCode]?.name ?? 'unknown'),
                group: groupCode,
                value: nb,
            })),
            label: { show: false },
            top: 30,
        },
    ]), [listCount, parameterGroupsIndexed])

    const totalQuantifications = useMemo(() => sumBy(series[0].data, 'value') ?? 0, [series])

    const getToolbox = () => {
        return {
            top: 20,
            right: '35px',
            showTitle: false,
            itemSize: 18,
            tooltip: {
                show: true,
                position: 'bottom',
            },
            feature: {
                myToolFullScreen: {
                    show: displayToolbox.current,
                    title: i18n.fullScreen,
                    icon: isFullScreen ? fullScreenExitIcon : fullScreenIcon,
                    onclick: toggleFullSCreen,
                },
                saveAsImage: {
                    show: displayToolbox.current,
                    title: i18n.export,
                    icon: exportPictureIcon,
                    name: i18n.distributionQuantificationsByFamily,
                },
                myToolExport: {
                    show: displayToolbox.current,
                    title: i18n.excelExport,
                    icon: exportExcelIcon,
                    onclick: () => {
                        const exportData = listCount.map(({ groupCode, nb }) => ({
                            name: getI18nOrLabel(parameterGroupsIndexed[groupCode]?.name ?? 'unknown'),
                            value: nb,
                        }))
                        const sortedData = orderBy(exportData, ['value'], ['desc'])
                        exportFile({
                            data: exportData.length ? [
                                {
                                    ...sortedData[0],
                                    headers: keys(sortedData[0]),
                                    customHeaders: ['parameterGroup', 'value'],
                                },
                                ...sortedData.slice(1),
                            ] : [],
                            exportType: 'xlsx',
                            titleFile: i18n.distributionQuantificationsByFamily,
                        }, true)
                    },
                },
            },
        }
    }

    const options = {
        title: {
            text: title,
            left: 'center',
        },
        tooltip: {
            trigger: 'item',
            formatter: ({ name, value, percent }) => {
                const wholeText = `${name} : ${value} quantifications (${percent}%)`
                return wholeText.split(' ').reduce((acc, word) => {
                    const lineLength = word.length + acc.length
                    return lineLength >= 100 ? { msg: `${acc.msg} + ${word}<br>`, length: acc.length - 100 } : { msg: `${acc.msg} ${word}`, length: lineLength }
                }, { msg: '', length: 0 }).msg
            },
        },
        legend: {
            type: 'scroll',
            orient: 'vertical',
            right: 60,
            top: 50,
            bottom: 20,
            formatter: name => {
                const bar = series[0].data?.find(d => d.name == name)
                const legend = `${bar.name} (${round(bar.value / totalQuantifications * 100, 2)}%)`
                return breakLine(legend)
            },
        },
        series,
        toolbox: getToolbox(),
    }

    return (
        <>
            <div
                onMouseOver={() => {
                    displayToolbox.current = true
                    echartRef.current?.getEchartsInstance().setOption({
                        toolbox: getToolbox(),
                    })
                }}
                onMouseOut={() => {
                    displayToolbox.current = false
                    echartRef.current?.getEchartsInstance().setOption({
                        toolbox: getToolbox(),
                    })
                }}
            >
                <ReactECharts
                    echarts={echarts}
                    option={options}
                    notMerge={true}
                    lazyUpdate={true}
                    className={'row no-margin'}
                    style={{ height: 300 }}
                    ref={e => {
                        echartRef.current = e
                    }}
                    onEvents={{
                        click: (params) => {
                            setPopinData(params.data)
                            openGraph()
                        },
                    }}
                />
            </div>
            <LargerPropertiesParameterGraphModal
                isOpen={isGraphOpen}
                closeGraph={closeGraph}
                parameters={popinParameters}
                qualitometers={qualitoIds}
                thresholds={[]}
                startPeriod={startPeriod}
                endPeriod={endPeriod}
                quantificationControl
                excludeProducers={excludeProducers}
                graphOptions={{ graphicTitle: `${i18n.quantificationsOn} ${i18n.parameterGroup}` }}
                dialogTitle={popinData?.name}
            />
            <Dialog
                fullScreen
                open={isFullScreen}
            >
                <DialogContent>
                    <div
                        onMouseOver={() => {
                            displayToolbox.current = true
                            echartDialogRef.current?.getEchartsInstance().setOption({
                                toolbox: getToolbox(),
                            })
                        }}
                        onMouseOut={() => {
                            displayToolbox.current = false
                            echartDialogRef.current?.getEchartsInstance().setOption({
                                toolbox: getToolbox(),
                            })
                        }}
                    >
                        <ReactECharts
                            echarts={echarts}
                            option={options}
                            notMerge={true}
                            lazyUpdate={true}
                            className={'row no-margin'}
                            style={{ height: '75vh' }}
                            ref={e => {
                                echartDialogRef.current = e
                            }}
                            onEvents={{
                                click: (params) => {
                                    setPopinData(params.data)
                                    openGraph()
                                },
                            }}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeFullScreen} variant='contained'>
                        {i18n.close}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

Pie.propTypes = {
    listCount: PropTypes.arrayOf(PropTypes.shape({
        groupCode: PropTypes.number,
        nb: PropTypes.number,
    })),
    title: PropTypes.string,
    qualitoIds: PropTypes.arrayOf(PropTypes.string),
    startPeriod: PropTypes.number,
    endPeriod: PropTypes.number,
}

const PieFamilyPesticides = ({
    ids = [],
    filters = {},
    title,
}) => {
    const excludeProducers = useApplicationSetting('CATCHMENT_PRODUCERS_EXCLUDED', setting => setting?.split(',').filter(c => !!c).map(id => parseInt(id)) || [])
    const relevantPesticides = useApplicationSetting(CATCHMENT_PESTICIDES, listStringParser)
    const irrelevantPesticides = useApplicationSetting(CATCHMENT_IRRELEVANT_PESTICIDES, listStringParser)
    const pesticides = useMemo(() => uniq([...relevantPesticides, ...irrelevantPesticides]), [relevantPesticides, irrelevantPesticides])

    const countFilters = useMemo(() => {
        return {
            lightMode: true,
            startDate: filters.firstStart,
            endDate: filters.firstEnd,
            stations: ids,
            parameters: pesticides,
            quantificationControl: true,
            excludedProducers: excludeProducers,
            support: '3', // support 'eau'
        }
    }, [excludeProducers, filters.firstEnd, filters.firstStart, ids, pesticides])

    const {
        controllerRef,
        initController,
    } = useAbortController()

    const {
        value: isLoaded,
        setFalse: setNotLoaded,
        setTrue: setLoaded,
    } = useBoolean(false)

    const [countGroupParameters, setCountGroupParameters] = useState([])

    useEffect(() => {
        if (countFilters.stations.length === 0 || isNil(countFilters.startDate) || isNil(countFilters.endDate) || countFilters.parameters.length === 0) return

        setNotLoaded()
        initController()
        setCountGroupParameters([])

        CatchmentAction.countGroupParameters(countFilters, controllerRef.current.signal)
            .catch(() => [])
            .then(setCountGroupParameters)
            .finally(() => !controllerRef.current.signal.aborted && setLoaded())

        // eslint-disable-next-line consistent-return
        return () => {
            controllerRef.current.abort()
        }
    }, [countFilters])

    return (
        <div style={{ position: 'relative' }}>
            {!isLoaded && (<CircularProgressWithLabel />)}
            <Pie
                title={title}
                listCount={!ids?.length ? [] : countGroupParameters}
                qualitoIds={ids}
                startPeriod={filters.firstStart}
                endPeriod={filters.firstEnd}
            />
        </div>
    )
}

PieFamilyPesticides.propTypes = {
    ids: PropTypes.arrayOf(PropTypes.number),
    filters: PropTypes.shape({}),
    title: PropTypes.string,
}

const GraphicEvolution = ({
    firstAnalysis = [],
    secondAnalysis = [],
    thresholds = [80, 40, 10, -10, -40, -80],
    title = '',
    criteria = LAST_VALUE,
    colors = colorGradients[0],
}) => {
    let echartRef = useRef(undefined)
    let displayToolbox = useRef(false)

    const {
        value: isExportOpen,
        setTrue: openExport,
        setFalse: closeExport,
    } = useBoolean(false)

    const {
        settings,
    } = useSelector(store => ({
        settings: store.AdministrationReducer.settings,
    }), shallowEqual)

    const analysis = useMemo(() => [...firstAnalysis, ...secondAnalysis], [firstAnalysis, secondAnalysis])

    const firstValues = getUsedValues(firstAnalysis, criteria, settings)
    const secondValues = getUsedValues(secondAnalysis, criteria, settings)

    const formattedThresholds = useMemo(() => ([...thresholds, Number.NEGATIVE_INFINITY]), [thresholds])
    const noQuantificationSerie = useMemo(() => formattedThresholds.length.toString(), [formattedThresholds.length])
    const seriesIndex = useMemo(() => range(formattedThresholds.length), [formattedThresholds.length])

    // the key is a string due to the groupBy
    const qualitometerIds = uniqBy(analysis, 'qualitometer').map(a => `${a.qualitometer}`)

    const groupAnalysis = groupBy(qualitometerIds, id => {
        const first = firstValues.find(v => v.qualitometer === id)?.value
        const second = secondValues.find(v => v.qualitometer === id)?.value

        if (isNil(first) || isNil(second)) return noQuantificationSerie

        const diff = second - first
        const percent = first !== 0 ? diff * 100 / first : 0

        return min(seriesIndex.filter(nb => percent >= formattedThresholds[nb]))
    })

    const getBar = useCallback((key, group = [], color) => {
        const percentage = group.length && qualitometerIds.length ? group.length / qualitometerIds.length * 100 : 0
        return {
            value: [percentage, key, criteria],
            itemStyle: {
                color,
            },
            label: {
                show: true,
                position: 'right',
                color: blackColor,
                formatter: () => `${round(percentage, 1)} %`,
            },
        }
    }, [criteria, qualitometerIds.length])

    const labelBar = useMemo(() => seriesIndex.map(index => {
        if (index === 0) return `${i18n.moreThan} ${thresholds[index]}`
        if (index === seriesIndex.length - 1) return `${i18n.lessThan} ${thresholds[index - 1]}`
        if (index < seriesIndex.length) return `${i18n.between} ${thresholds[index - 1]} ${i18n.and} ${thresholds[index]}`
        return i18n.noQuantification
    }), [seriesIndex, thresholds])

    const criteriaTable = useMemo(() => ({
        0: i18n.lastValue,
        1: i18n.min,
        2: i18n.max,
        3: i18n.average,
        4: i18n.P90,
    }), [])

    const getToolbox = () => ({
        top: '4%',
        right: '35px',
        showTitle: false,
        itemSize: 18,
        tooltip: {
            show: true,
            position: 'bottom',
        },
        feature: {
            saveAsImage: {
                show: displayToolbox.current,
                title: i18n.pictureExport,
                icon: exportPictureIcon,
                name: `${i18n.evolution} ${title}`,
            },
            myToolExport: {
                show: displayToolbox.current,
                title: i18n.excelExport,
                icon: exportExcelIcon,
                onclick: openExport,
            },
        },
    })

    const options = {
        title: [
            {
                text: title,
                left: 'center',
                top: '10',
            },
            {
                subtext: `${i18n.evolutionPercent}`,
                left: 'left',
                subtextStyle: {
                    color: '222',
                },
                top: '30',
            },
        ],
        series: [{
            type: 'bar',
            data: [
                ...seriesIndex.map(i => getBar(i.toString(), groupAnalysis[i.toString()], colors[i])),
                getBar(noQuantificationSerie, groupAnalysis[noQuantificationSerie], lightGrey),
            ],
        }],
        xAxis: [{
            type: 'value',
            name: `${i18n.catchmentPercentage} (%)`,
            nameLocation: 'middle',
            nameGap: 25,
        }],
        yAxis: [{
            type: 'category',
            data: [...seriesIndex, max(seriesIndex)+1].reverse(),
            axisLabel: {
                formatter: v => labelBar[v] ?? i18n.noQuantification,
            },
        }],
        grid: {
            top: '60',
            left: '5',
            right: '50',
            bottom: '80',
            containLabel: true,
            height: 200,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow',
                snap: true,
            },
            formatter: params => {
                if (params[0]?.value?.length < 2) return ''
                const [ evolution, serieIndex, graphCriteria ] = params[0].value
                if (serieIndex === noQuantificationSerie) return template(i18n.noEvolutionPercentExplanation)({ evolution: round(evolution, 1) })
                return template(i18n.evolutionPercentExplanation)({ evolution: round(evolution, 1), criteria: criteriaTable[graphCriteria], interval: labelBar[serieIndex]?.toLowerCase() })
            },
        },
        toolbox: getToolbox(),
    }
    return (
        <div
            onMouseOver={() => {
                displayToolbox.current = true
                echartRef.current?.getEchartsInstance().setOption({
                    toolbox: getToolbox(),
                })
            }}
            onMouseOut={() => {
                displayToolbox.current = false
                echartRef.current?.getEchartsInstance().setOption({
                    toolbox: getToolbox(),
                })
            }}
        >
            <ReactECharts
                echarts={echarts}
                option={options}
                notMerge={true}
                lazyUpdate={true}
                ref={e => {
                    echartRef.current = e
                }}
            />
            <ExportModal
                isOpen={isExportOpen}
                close={closeExport}
                analysis={analysis}
                exportName={`${i18n.evolution} ${title}`}
            />
        </div>
    )
}

GraphicEvolution.propTypes = {
    firstAnalysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)),
    secondAnalysis: PropTypes.arrayOf(PropTypes.instanceOf(DtoAnalysisLight)),
    thresholds: PropTypes.arrayOf(PropTypes.number),
    title: PropTypes.string,
    criteria: PropTypes.string,
    colors: PropTypes.arrayOf(PropTypes.string),
}

const ParameterGraphicEvolution = ({
    parameter,
    ids = [],
    filters = {},
    colors = colorGradients[0],
    thresholds = [80, 40, 10, -10, -40, -80],
    title = '',
}) => {
    const {
        parametersProps,
    } = useSelector(store => ({
        parametersProps: store.ParameterReducer.parameters,
    }), shallowEqual)

    const parametersIndexed = useListIndexed(parametersProps, 'code')

    const excludeProducers = useApplicationSetting('CATCHMENT_PRODUCERS_EXCLUDED', setting => setting?.split(',').filter(c => !!c).map(id => parseInt(id)) || [])

    const firstAnalysisFilters = useMemo(() => {
        return {
            lightMode: true,
            startDate: filters.firstStart,
            endDate: filters.firstEnd,
            stations: ids,
            parameters: [parameter],
            excludedProducers: excludeProducers,
            support: '3', // support 'eau'
        }
    }, [excludeProducers, filters.firstEnd, filters.firstStart, ids, parameter])

    const {
        analysis: firstAnalysis = [],
        isLoaded: firstIsLoaded = false,
        progress: firstProgress = 0,
    } = useFetchAnalysis(firstAnalysisFilters)

    const secondAnalysisFilters = useMemo(() => {
        return {
            lightMode: true,
            startDate: filters.secondStart,
            endDate: filters.secondEnd,
            stations: ids,
            parameters: [parameter],
            excludedProducers: excludeProducers,
            support: '3', // support 'eau'
        }
    }, [excludeProducers, filters.secondEnd, filters.secondStart, ids, parameter])

    const {
        analysis: secondAnalysis = [],
        isLoaded: secondIsLoaded = false,
        progress: secondProgress = 0,
    } = useFetchAnalysis(secondAnalysisFilters)

    return (
        <div style={{ position: 'relative' }}>
            {(!firstIsLoaded || !secondIsLoaded) && (<CircularProgressWithLabel value={(firstProgress + secondProgress) / 2}/>)}
            <GraphicEvolution
                firstAnalysis={!ids.length ? [] : firstAnalysis}
                secondAnalysis={!ids.length ? [] : secondAnalysis}
                title={title || parametersIndexed[parameter]?.name}
                criteria={filters.criteria}
                colors={colors}
                thresholds={thresholds}
            />
        </div>
    )
}

ParameterGraphicEvolution.propTypes = {
    parameter: PropTypes.string,
    ids: PropTypes.arrayOf(PropTypes.number),
    filters: PropTypes.shape({}),
    colors: PropTypes.arrayOf(PropTypes.string),
    thresholds: PropTypes.arrayOf(PropTypes.number),
    title: PropTypes.string,
}

const SumParameterGraphicEvolution = ({
    ids = [],
    filters = {},
    colors = colorGradients[0],
    thresholds = [80, 40, 10, -10, -40, -80],
    title = '',
}) => {
    const excludeProducers = useApplicationSetting('CATCHMENT_PRODUCERS_EXCLUDED', setting => setting?.split(',').filter(c => !!c).map(id => parseInt(id)) || [])
    const relevantPesticides = useApplicationSetting(CATCHMENT_PESTICIDES, listStringParser)
    const irrelevantPesticides = useApplicationSetting(CATCHMENT_IRRELEVANT_PESTICIDES, listStringParser)
    const pesticides = useMemo(() => uniq([...relevantPesticides, ...irrelevantPesticides]), [relevantPesticides, irrelevantPesticides])
    const listSumPesticides = useApplicationSetting(CATCHMENT_LIST_SUM_PESTICIDES, listStringParser)

    const firstAnalysisFilters = useMemo(() => {
        return {
            lightMode: true,
            startDate: filters.firstStart,
            endDate: filters.firstEnd,
            stations: ids,
            parameters: listSumPesticides.length > 0 ? listSumPesticides : pesticides,
            quantificationControl: true,
            excludedProducers: excludeProducers,
            support: '3', // support 'eau'
        }
    }, [excludeProducers, filters.firstEnd, filters.firstStart, ids, listSumPesticides, pesticides])

    const {
        sumAnalysis: firstSumAnalysis = [],
        isLoaded: firstIsLoaded = false,
        progress: firstProgress = 0,
    } = useFetchSumAnalysis(firstAnalysisFilters)

    const secondAnalysisFilters = useMemo(() => {
        return {
            lightMode: true,
            startDate: filters.secondStart,
            endDate: filters.secondEnd,
            stations: ids,
            parameters: listSumPesticides.length > 0 ? listSumPesticides : pesticides,
            quantificationControl: true,
            excludedProducers: excludeProducers,
            support: '3', // support 'eau'
        }
    }, [excludeProducers, filters.secondEnd, filters.secondStart, ids, listSumPesticides, pesticides])

    const {
        sumAnalysis: secondSumAnalysis = [],
        isLoaded: secondIsLoaded = false,
        progress: secondProgress = 0,
    } = useFetchSumAnalysis(secondAnalysisFilters)

    return (
        <div style={{ position: 'relative' }}>
            {(!firstIsLoaded || !secondIsLoaded) && (<CircularProgressWithLabel value={(firstProgress + secondProgress) / 2} />)}
            <GraphicEvolution
                firstAnalysis={!ids.length ? [] : firstSumAnalysis}
                secondAnalysis={!ids.length ? [] : secondSumAnalysis}
                title={title || i18n.sumPesticides}
                criteria={filters.criteria}
                colors={colors}
                thresholds={thresholds}
            />
        </div>
    )
}

SumParameterGraphicEvolution.propTypes = {
    parameter: PropTypes.string,
    ids: PropTypes.arrayOf(PropTypes.number),
    filters: PropTypes.shape({}),
    colors: PropTypes.arrayOf(PropTypes.string),
    thresholds: PropTypes.arrayOf(PropTypes.number),
    title: PropTypes.string,
}

export {
    GraphicPercent,
    ParameterGraphicPercent,
    SumParameterGraphicPercent,
    PieFamilyPesticides,
    GraphicEvolution,
    ParameterGraphicEvolution,
    SumParameterGraphicEvolution,
}